/**
 * formatDate / formatDateRange
 *
 * Formats ISO datetime strings (e.g. "2026-07-23T00:00:00.000000Z", as
 * SSS's real API returns) or plain date strings (e.g. "2026-07-23", as
 * the sample/xlsx data uses) into "23 Jul 2026" -- consistently,
 * regardless of which shape the input is in. Parsed/formatted in UTC
 * deliberately: a date-only input like "2026-07-23" parsed as local time
 * in a negative-UTC-offset timezone would roll back to the 22nd, which
 * a date-only value (no real time-of-day meaning) shouldn't do.
 */

/**
 * @param {string|null|undefined} input
 * @returns {string|null} e.g. "23 Jul 2026", or null if input is empty/unparseable
 */
export function formatDate(input) {
  if (!input) return null;
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Formats a "<start> - <end>" range string (either side may be a full
 * ISO datetime or a plain date) into "23 Jul 2026 - 07 Feb 2026" style.
 * Non-range values (no " - " separator) are formatted as a single date.
 * @param {string|null|undefined} rangeString
 * @returns {string} formatted range, or the original string if unparseable/empty
 */
export function formatDateRange(rangeString) {
  if (!rangeString || typeof rangeString !== 'string') return rangeString ?? '\u2014';

  const parts = rangeString.split(' - ');
  if (parts.length === 2) {
    const [start, end] = parts;
    return `${formatDate(start) ?? start} - ${formatDate(end) ?? end}`;
  }

  return formatDate(rangeString) ?? rangeString;
}
